import { useEffect, useState } from 'react';
import { formatCurrency, formatDate } from '@/lib/format.js';
import Panel from './Panel.jsx';
import { getDealTransactions, markPaymentReturned, getDealCredits, getDeferredCollections } from '@/services/deals.service.js';
import { deriveMoneyStrip } from '@/lib/moneyStrip.js';
import { Button } from '@/components/ui/button.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { Undo2 } from 'lucide-react';
import CommissionRateInline, { canStateRate } from '@/components/money/CommissionRateInline.jsx';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog.jsx';

// IS THE MONEY RIGHT (MV-10 3.2 zone 3), interim strip for Ship 4a:
// invoiced / paid / owed straight off the policy_transactions ledger, with
// honest method labels (ACH pending is not paid; waived is not cash). This
// is deliberately a THIN derivation: MV-08 (Ship 5) replaces the internals
// with the full three-pane + integrity verdict behind this same seam; the
// zone position and component boundary stay. The math lives in
// @/lib/moneyStrip.js (unit-tested); a provisional (unsigned, unpaid) bind
// from the tower flow does not read as invoiced until the client signs or pays.

export { deriveMoneyStrip };

const METHOD_LABEL = { ach: 'ACH', card: 'card', check: 'check', unknown: 'unrecorded method' };

function Tile({ label, value, tone }) {
  return (
    <div className="min-w-[110px] flex-1 rounded-card border border-border bg-bg/40 px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-text-dim">{label}</p>
      <p className={`mt-0.5 font-mono text-[15px] ${tone || 'text-text'}`}>{value}</p>
    </div>
  );
}

export default function MoneyStrip({ dealId, refreshKey, onReversed }) {
  const [txns, setTxns] = useState(null);
  const [failed, setFailed] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reversing, setReversing] = useState(false);
  // Fronted credits (D8 honesty, REWRITE-AND-CREDITS-SPEC §B5): applied/settled
  // credits on this deal whose source carrier remit hasn't landed yet.
  const [frontedCredits, setFrontedCredits] = useState([]);
  // ACCOUNT-SCOPED DEFERRALS. The Deferred tile above is deal-scoped, derived
  // from THIS deal's transactions, so a renewal deal with no ledger of its own
  // showed nothing at all while the account was holding $164 on the expiring
  // policy. Silence meant both "nothing deferred" and "deferred, but you cannot
  // have it yet" (David, 2026-08-19). The server words the distinction; this
  // renders it. `undefined` is still loading, `null` is a FAILED read, and the
  // two must never look like the empty case.
  const [deferredListing, setDeferredListing] = useState(undefined);
  // Bumped when a commission rate is stated, so the strip re-reads the ledger.
  const [rateTick, setRateTick] = useState(0);

  useEffect(() => {
    if (!dealId) return;
    setFailed(false);
    getDealTransactions(dealId)
      .then((d) => setTxns(d.transactions || []))
      .catch(() => { setTxns([]); setFailed(true); });
    getDealCredits(dealId)
      .then((r) => setFrontedCredits((r?.credits || []).filter((c) => c.fronted)))
      .catch(() => setFrontedCredits([]));
    setDeferredListing(undefined);
    getDeferredCollections(dealId)
      .then((r) => setDeferredListing(r || null))
      // A FAILED READ IS NOT AN EMPTY ACCOUNT. Falling back to an empty listing
      // here would print the same silence the whole change exists to remove.
      .catch(() => setDeferredListing(null));
  }, [dealId, refreshKey, rateTick]);

  // Rendered whether or not this deal has a ledger: the founding case is a
  // renewal deal with NO transactions of its own and money owed on the account.
  const deferredNote = deferredListing === undefined
    ? null
    : deferredListing === null
      ? 'Could not check this account for deferred items.'
      : (deferredListing.sentence || null);
  const deferredState = deferredListing?.summary?.state || null;

  const m = txns === null ? null : deriveMoneyStrip(txns);
  // Reversal only makes sense when the ledger already carries money (paid or
  // ACH in flight) — a returned/declined Nickel payment walks it back.
  const hasMoney = !!m && (m.paid > 0.005 || m.pending > 0.005);

  async function handleReverse() {
    if (!dealId || reversing) return;
    setReversing(true);
    try {
      const res = await markPaymentReturned(dealId, { reason: 'ACH returned' });
      if (!res?.success) { toast.error(res?.error || 'Nothing to reverse'); return; }
      toast.success('Payment reversed — deal returned to awaiting payment');
      setConfirmOpen(false);
      onReversed?.();
    } catch (e) {
      toast.error(e?.body?.error || e?.message || 'Reversal failed');
    } finally {
      setReversing(false);
    }
  }

  return (
    <Panel
      title="Is the money right"
      empty={
        txns === null
          ? 'Reading the ledger…'
          : failed
            ? 'Ledger unavailable right now.'
            : !m.hasLedger
              ? 'No ledger rows yet. The term’s bind transaction lands here at bind; the full money verdict arrives with Ship 5.'
              : null
      }
    >
      {/* OUTSIDE the hasLedger gate on purpose. Inside it, the one case this
          exists for -- a renewal deal with no ledger rows and an account
          holding money back -- renders nothing, which is where it started. */}
      {deferredNote ? (
        <p className={`mb-2 text-[11px] ${deferredListing === null ? 'text-danger' : deferredState === 'not_yet' ? 'text-text-muted' : 'text-warning'}`}>
          {deferredNote}
        </p>
      ) : null}
      {m?.hasLedger ? (
        <div>
          <div className="flex flex-wrap gap-2">
            <Tile label="Invoiced" value={formatCurrency(m.invoiced)} />
            <Tile label="Paid" value={formatCurrency(m.paid)} tone={m.paid > 0 ? 'text-success' : undefined} />
            {m.pending > 0 ? <Tile label="In flight" value={formatCurrency(m.pending)} tone="text-warning" /> : null}
            {m.waived > 0 ? <Tile label="Waived" value={formatCurrency(m.waived)} /> : null}
            {/* Financed (190): its own word. The finance company paid, so it is
                not Paid (no cash reached us) and not Owed (the client is done). */}
            {m.financed > 0 ? <Tile label="Financed" value={formatCurrency(m.financed)} tone="text-info" /> : null}
            {/* Absorbed: the client paid short and the agency ate it. Not owed by anyone. */}
            {m.absorbed > 0 ? <Tile label="Absorbed" value={formatCurrency(m.absorbed)} tone="text-text-muted" /> : null}
            {/* Deferred stays inside Owed (it IS unpaid) and gets its own
                tile so David's radar carries the reason. Broker surface only:
                the client never sees this word (David 2026-08-12). */}
            {m.deferred > 0 ? <Tile label="Deferred" value={formatCurrency(m.deferred)} tone="text-warning" /> : null}
            <Tile
              label="Owed"
              value={formatCurrency(m.owed)}
              tone={m.owed > 0.005 ? 'text-warning' : m.owed < -0.005 ? 'text-info' : 'text-text-muted'}
            />
          </div>
          {/* COMMISSION, WHERE THE MONEY IS READ (David 2026-10-01). The rate
              line lived only in the collapsed Trust and transactions fold at
              the bottom of the page, which is nowhere. One line per live row
              that carries a premium; an unstated rate says so here. */}
          {(txns || []).filter((t) => !t.voided_at && t.type !== 'correction' && t.premium_delta != null && (canStateRate(t) || t.commission_rate != null)).map((t) => (
            <CommissionRateInline
              key={t.id}
              txn={t}
              label={`${t.type === 'bind' ? 'Bind' : t.type} ${formatDate(t.effective_date)}`}
              onSaved={() => setRateTick((n) => n + 1)}
            />
          ))}
          {m.payments.length > 0 ? (
            <p className="mt-2 text-[11px] text-text-muted">
              {m.payments.map((p, i) => (
                <span key={i}>
                  {i > 0 ? ' · ' : ''}
                  {formatCurrency(p.amount)} by {METHOD_LABEL[String(p.method).toLowerCase()] || p.method}
                  {p.at ? ` on ${formatDate(p.at)}` : ''}
                  {p.pending ? ' (ACH pending, not yet cleared)' : ''}
                </span>
              ))}
            </p>
          ) : null}
          {m.owed < -0.005 ? (
            <p className="mt-1 text-[11px] text-info">Paid exceeds invoiced; expect a return premium or a ledger correction.</p>
          ) : null}
          {(m.deferrals || []).length > 0 ? (
            <p className="mt-1 text-[11px] text-warning">
              {m.deferrals.map((d, i) => (
                <span key={i}>{i > 0 ? ' · ' : ''}Deferred {formatCurrency(d.amount)}{d.reason ? `: ${d.reason}` : ''}</span>
              ))}
            </p>
          ) : null}
          {frontedCredits.length > 0 ? (
            <p className="mt-1 text-[11px] text-warning">
              Fronting {formatCurrency(frontedCredits.reduce((s, c) => s + Number(c.amount || 0), 0))} in applied credit — carrier remit pending on the source return.
            </p>
          ) : null}
          {hasMoney ? (
            <div className="mt-2.5">
              <Button
                size="sm"
                variant="ghost"
                className="justify-start gap-2 text-text-muted hover:text-warning"
                onClick={() => setConfirmOpen(true)}
              >
                <Undo2 size={14} strokeWidth={1.5} />
                <span className="text-xs">Payment returned / declined</span>
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reverse this payment?</AlertDialogTitle>
            <AlertDialogDescription>
              Use this when Nickel reports the payment returned or declined (ACH account closed, chargeback).
              The A/R walks back to failed and the deal returns to awaiting payment. The client’s existing
              payment link resumes at the Nickel step so they can re-pay. Nothing is emailed to the client.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reversing}>Keep as paid</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleReverse(); }}
              disabled={reversing}
            >
              {reversing ? 'Reversing…' : 'Reverse payment'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Panel>
  );
}
